import { MDSLoadingSpinner } from "@/mds/components/loading/loading-spinner";
import { MDSBubble, MDSBubbleAlignment, MDSBubbleVariant } from "@/mds/components/bubble";
import { LoadingContainer } from "@/components/note/editor/source-bar/floating-preview-content.styles";
import { observer } from "mobx-react-lite";
import { GridList, GridListItem } from "react-aria-components";
import styled from "@emotion/styled";
import { useEffect, useRef } from "react";
import { DateTime } from "luxon";
import { NoteSourceTextMessageArtifactData } from "@/store/sources/types";
import { FloatingTextMessageMediaButton } from "@/components/note/editor/source-bar/components/floating-text-message-media-button";
import { ExtendedNoteSourceArtifactInfo } from "@/components/note/editor/source-bar/types";

interface FloatingTextMessagePreviewContentProps {
  isOpen: boolean;
  isLoading: boolean;
  extendedArtifactInfo: ExtendedNoteSourceArtifactInfo;
}

type TextMessageItemMedia = {
  content_type: string;
  public_url: string;
};

type TextMessageItem = {
  id: string;
  primaryLabel: string;
  sentAt: DateTime;
  isSystemMessage: boolean;
  isLastInGroup: boolean;
  mediaLinks: TextMessageItemMedia[];
};

export const FloatingTextMessagePreviewContent = observer<FloatingTextMessagePreviewContentProps>(
  ({ isLoading, extendedArtifactInfo }) => {
    const gridListRef = useRef<HTMLDivElement>(null);

    const allTextMessageItems: (TextMessageItem | null)[] =
      extendedArtifactInfo?.data?.map((artifactInfo, index) => {
        if (!artifactInfo) {
          return null;
        }

        if (artifactInfo.artifact_kind !== "TEXT_MESSAGE") {
          return null;
        }

        const data = artifactInfo.value as NoteSourceTextMessageArtifactData;

        // The fallback is there for completeness, but shouldn't happen
        const artifactId = artifactInfo.id;

        const mediaLinks =
          data.media?.map(media => ({
            content_type: media.content_type,
            public_url: media.public_url,
          })) ?? [];

        // TODO: take into account isSystemMessage vs user message to build "groups"
        // For now, all messages are sent from the user (so there is only one group)
        const isSystemMessage = false;
        const isLastInGroup = index === (extendedArtifactInfo?.data?.length ?? 0) - 1;

        return {
          id: artifactId,
          primaryLabel: data.text,
          isSystemMessage,
          isLastInGroup,
          sentAt: DateTime.fromISO(data.received_at),
          mediaLinks,
        };
      }) ?? [];

    const items = allTextMessageItems.filter(item => item !== null);

    useEffect(() => {
      if (isLoading) return;

      if (gridListRef.current) {
        gridListRef.current.scrollTop = gridListRef.current.scrollHeight;
      }
    }, [isLoading, items]);

    return (
      <Content>
        {isLoading ? (
          <TextLoadingContainer>
            <MDSLoadingSpinner size={24} />
          </TextLoadingContainer>
        ) : (
          <StyledGridList ref={gridListRef} items={items} aria-label="Text message sources">
            {item => (
              <GridListItem>
                {item.isSystemMessage ? (
                  <MDSBubble
                    align={MDSBubbleAlignment.Left}
                    variant={MDSBubbleVariant.VoiceOthers}
                    isLastInGroup={item.isLastInGroup}
                  >
                    {item.primaryLabel}
                    <StyledBubbleTimestamp>
                      {item.sentAt.toLocaleString(DateTime.TIME_SIMPLE)} •{" "}
                      {item.sentAt.toLocaleString({ month: "short", day: "numeric" })}
                    </StyledBubbleTimestamp>
                  </MDSBubble>
                ) : (
                  <MDSBubble
                    align={MDSBubbleAlignment.Right}
                    variant={MDSBubbleVariant.VoiceSelf}
                    isLastInGroup={item.isLastInGroup}
                  >
                    {item.primaryLabel}
                    {item.mediaLinks.map(media => (
                      <FloatingTextMessageMediaButton
                        key={media.public_url}
                        contentType={media.content_type}
                        publicUrl={media.public_url}
                      />
                    ))}
                    <StyledBubbleTimestamp>
                      {item.sentAt.toLocaleString(DateTime.TIME_SIMPLE)} •{" "}
                      {item.sentAt.toLocaleString({ month: "short", day: "numeric" })}
                    </StyledBubbleTimestamp>
                  </MDSBubble>
                )}
              </GridListItem>
            )}
          </StyledGridList>
        )}
      </Content>
    );
  }
);

export const TextLoadingContainer = styled(LoadingContainer)`
  min-height: 350px;
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  flex-grow: 1;
  width: min(350px, 90vw);
  max-width: 90vw;
  padding: ${({ theme }) => theme.spacing["4"]};
`;

const StyledGridList = styled(GridList)`
  min-height: 350px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding: ${({ theme }) => theme.spacing["2"]};
  max-height: 400px;
  overflow-y: auto;
  flex-grow: 1;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
` as typeof GridList;

const StyledBubbleTimestamp = styled.div`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  line-height: ${({ theme }) => theme.lineHeights["paragraph-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;
